"use client";

import Flag from "react-world-flags";
import {
    LuMemoryStick,
    LuHardDrive,
    LuGauge,
    LuCpu,
    LuActivity,
    LuExternalLink,
} from "react-icons/lu";
import type { IconType } from "react-icons";
import { PingChip } from "./ping-ui";
import type { Cpu, Location, Reading } from "./types";

/** One labelled spec: icon tile, caption, value. */
function Spec({ Icon, label, value }: { Icon: IconType; label: string; value: string }) {
    return (
        <div className="flex gap-2.5">
            <span
                className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center"
                style={{ background: "rgba(124,58,237,0.16)" }}
            >
                <Icon size={12} className="text-purple-300" />
            </span>
            <div className="min-w-0">
                <p className="text-gray-500 font-poppins text-[10px] uppercase tracking-wider leading-none">{label}</p>
                <p className="text-gray-200 font-poppins text-xs mt-1 truncate">{value}</p>
            </div>
        </div>
    );
}

/**
 * One CPU node: which location it sits in and your latency to that region, then
 * its specs. The lowest-latency location's nodes carry the recommendation ribbon.
 */
export default function NodeCard({
    location,
    region,
    cpu,
    reading,
    recommended,
    onRetest,
}: {
    location: Location;
    region: string;
    cpu: Cpu;
    reading: Reading;
    recommended: boolean;
    onRetest: () => void;
}) {
    return (
        <div
            className="relative w-full rounded-2xl p-4 pt-5 flex flex-col transition-colors duration-200"
            style={{
                background: "linear-gradient(150deg, #26103c 0%, #170a26 55%, #0d0d14 100%)",
                border: recommended ? "1px solid rgba(168,85,247,0.55)" : "1px solid #7c44e511",
                boxShadow: recommended ? "0 0 40px rgba(124,58,237,0.18)" : "none",
            }}
        >
            {recommended && (
                <span
                    className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-0 whitespace-nowrap rounded-md px-2.5 py-1 font-poppins text-[9px] font-semibold uppercase tracking-wider text-white"
                    style={{ background: "linear-gradient(90deg, #6416a3 0%, #7c3aed 100%)" }}
                >
                    Recommended for you
                </span>
            )}

            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                    <Flag
                        code={location.code}
                        alt={`${location.country} flag`}
                        style={{ width: 22, height: 15, borderRadius: 2, objectFit: "cover", imageRendering: "pixelated" }}
                    />
                    <p className="text-white font-poppins text-sm font-semibold truncate">
                        {location.city}, {region}
                    </p>
                </div>
                <PingChip reading={reading} soon={location.soon} onRetest={onRetest} />
            </div>

            <p className="text-white font-poppins text-sm font-semibold mt-4">{cpu.name}</p>
            <span className="mt-2 self-start inline-flex items-center gap-1.5 font-poppins text-[11px] text-purple-300">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                Performance node
            </span>

            <div
                className="mt-3.5 rounded-lg p-3.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3.5"
                style={{ background: "rgba(0,0,0,0.28)", border: "1px solid rgba(255,255,255,0.05)" }}
            >
                <Spec Icon={LuMemoryStick} label="RAM Type" value={cpu.ram} />
                <Spec Icon={LuHardDrive} label="Drive Type" value="NVMe SSD" />
                <Spec Icon={LuGauge} label="Clock Speed" value={cpu.clock} />
                <Spec Icon={LuCpu} label="PassMark CPU Mark" value={cpu.mark} />
                <Spec Icon={LuActivity} label="PassMark Single Thread" value={cpu.single} />
            </div>

            <a
                href={cpu.passmarkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 self-start inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-poppins text-xs text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 transition-colors"
            >
                View on PassMark
                <LuExternalLink size={11} />
            </a>
        </div>
    );
}
